import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useBlogPost, getRelatedPosts } from "@/data/blogPosts";
import { StatsBlock, extractStatsBlocks } from "@/components/blog/StatsBlock";
import { Clock, Calendar, Tag, ArrowRight, Share2, FileDown, FileSpreadsheet } from "lucide-react";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { post, loading } = useBlogPost(slug);
  const relatedPosts = post ? getRelatedPosts(post.slug) : [];

  if (loading && !post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
            <p className="text-muted-foreground mb-6">The article you're looking for doesn't exist.</p>
            <Button asChild>
              <Link to="/blog">Back to Blog</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: post.date,
    author: { "@type": "Person", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "Upfixe Ads",
      url: "https://upfixe.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://upfixe.com/blog/${post.slug}`,
    },
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{post.title} | Upfixe Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.keywords.join(", ")} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <link rel="canonical" href={`https://upfixe.com/blog/${post.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>
      <Header />

      <main className="flex-grow pt-24 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Hero */}
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-600 uppercase">{post.category}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">{post.title}</h1>
            <div className="flex items-center gap-6 text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.readTime} min read
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mb-12 rounded-lg overflow-hidden">
            <img src={post.image} alt={post.title} className="w-full h-96 object-cover" />
          </div>

          {/* Content (stats block lifted out, rendered as boxes above the prose) */}
          {(() => {
            const { cleaned, stats } = extractStatsBlocks(post.content);
            return (
              <>
                <StatsBlock stats={stats} />
                <div className="prose prose-lg prose-blue max-w-none mb-12 prose-table:border prose-th:bg-blue-50 prose-th:px-4 prose-th:py-2 prose-td:px-4 prose-td:py-2 prose-td:border-t">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {cleaned}
                  </ReactMarkdown>
                </div>
              </>
            );
          })()}

          {/* Downloads (auto-published experiment posts only) */}
          {(post.pdfUrl || (post.csvDownloads && post.csvDownloads.length > 0)) && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-12">
              <h3 className="font-semibold mb-3 text-blue-900">Download the experiment data</h3>
              <p className="text-sm text-blue-900/70 mb-4">
                Original campaign data from this experiment — keywords, bid estimates, ad groups, and ad copy.
              </p>
              <div className="flex flex-wrap gap-3">
                {post.pdfUrl && (
                  <Button variant="outline" asChild>
                    <a href={post.pdfUrl} target="_blank" rel="noopener noreferrer">
                      <FileDown className="w-4 h-4 mr-2" />
                      Comparison PDF
                    </a>
                  </Button>
                )}
                {post.csvDownloads?.map((csv) => (
                  <Button key={csv.url} variant="outline" asChild>
                    <a href={csv.url} target="_blank" rel="noopener noreferrer">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      {csv.label}
                    </a>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Share Buttons */}
          <div className="border-t border-b py-6 mb-12 flex items-center gap-4">
            <span className="font-semibold">Share this article:</span>
            <Button variant="outline" size="sm" asChild>
              <a href={`https://twitter.com/intent/tweet?url=https://upfixe.com/blog/${post.slug}&text=${post.title}`} target="_blank" rel="noopener noreferrer">
                <Share2 className="w-4 h-4 mr-2" />
                Twitter
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=https://upfixe.com/blog/${post.slug}`} target="_blank" rel="noopener noreferrer">
                <Share2 className="w-4 h-4 mr-2" />
                LinkedIn
              </a>
            </Button>
          </div>

          {/* Author Info */}
          <div className="bg-blue-50 rounded-lg p-6 mb-12">
            <h3 className="font-semibold mb-2">About the Author</h3>
            <p className="text-muted-foreground">{post.author} is the founder of Upfixe, helping businesses scale their Google Ads through intelligent automation.</p>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-8">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link key={relatedPost.slug} to={`/blog/${relatedPost.slug}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                      <CardHeader>
                        <span className="text-xs font-semibold text-blue-600 uppercase mb-2">{relatedPost.category}</span>
                        <CardTitle className="text-lg">{relatedPost.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">{relatedPost.excerpt}</p>
                        <div className="flex items-center gap-2 text-blue-600 font-semibold">
                          Read More <ArrowRight className="w-4 h-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Optimize Your Google Ads?</h2>
            <p className="text-lg mb-6 opacity-90">
              Apply these strategies with Upfixe's AI-powered platform. Start free today.
            </p>
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" asChild>
              <Link to="/auth?signup=true">Start Free</Link>
            </Button>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
